const HEIC_MIME_TYPES = ["image/heic", "image/heif"];
const HEIC_EXTENSIONS = [".heic", ".heif"];

/**
 * Whether a MIME type is HEIC/HEIF. Use this when only the recorded type is available, such as a
 * stored file record; prefer {@link isHEIC} when the `File` itself is in hand, since that also
 * catches files whose type the browser left blank.
 */
export const isHEICType = (type?: string): boolean => HEIC_MIME_TYPES.includes(type?.toLowerCase() ?? "");

/**
 * Whether a file is HEIC/HEIF, the format iOS cameras produce by default.
 *
 * Browsers frequently report an empty MIME type for these files, so the filename extension is
 * checked as a fallback.
 */
export const isHEIC = (file: File): boolean => {
  if (isHEICType(file.type)) return true;
  const name = file.name.toLowerCase();
  return HEIC_EXTENSIONS.some((ext) => name.endsWith(ext));
};

/**
 * Converts a HEIC/HEIF file into a browser-renderable PNG blob, e.g. to show a preview of a
 * locally-selected file that the browser cannot display natively.
 *
 * Returns null for non-HEIC input and when decoding fails, so callers can fall back to showing no
 * preview.
 *
 * The decoder is imported lazily because it touches canvas and Worker at module scope: a static
 * import would break every non-browser consumer of this package's barrel (and its ~2.7MB would
 * load for callers that never decode a HEIC).
 *
 * @example
 * ```typescript
 * const preview = await convertHEIC(file);
 * if (preview) setPreviewUrl(URL.createObjectURL(preview));
 * ```
 */
export const convertHEIC = async (file: File): Promise<Blob | null> => {
  if (!isHEIC(file)) return null;

  try {
    const { default: heic2any } = await import("heic2any");
    const converted = await heic2any({ blob: file, toType: "image/png" });
    return Array.isArray(converted) ? converted[0] ?? null : converted;
  } catch {
    return null;
  }
};
